import React, { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { Trophy, Award, Star, PartyPopper } from 'lucide-react';
import { Button } from './ui/button';

/**
 * CelebrationOverlay - Shows a celebration animation with confetti, balloons, and ribbons
 * Triggered when user passes a post-test or completes a course
 * Color Theme: White, Blue, Gold
 */
export const CelebrationOverlay = ({ 
  isVisible, 
  onClose, 
  title = "Congratulations!",
  message = "You have successfully completed the course!",
  score,
  courseName,
  type = "course" // "course" | "post_test" | "module"
}) => {
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    if (isVisible) {
      // Delay content show for dramatic effect
      setTimeout(() => setShowContent(true), 300);
      
      // Fire confetti from multiple angles
      fireConfetti();
    } else {
      setShowContent(false);
    }
  }, [isVisible]);

  const fireConfetti = () => {
    const duration = 4000;
    const animationEnd = Date.now() + duration;
    // Theme colors: Blue and Gold
    const themeColors = ['#2563EB', '#3B82F6', '#60A5FA', '#FFD700', '#FFC107', '#FFFFFF'];
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };

    // Random confetti bursts
    const randomInRange = (min, max) => Math.random() * (max - min) + min;

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      // Confetti from both sides
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 },
        colors: themeColors
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 },
        colors: themeColors
      });
    }, 250);

    // Big center burst with gold and blue
    setTimeout(() => {
      confetti({
        particleCount: 100,
        spread: 100,
        origin: { x: 0.5, y: 0.5 },
        colors: ['#FFD700', '#2563EB', '#FFFFFF'],
        zIndex: 10000
      });
    }, 500);

    // Cannon shots from corners
    setTimeout(() => {
      confetti({
        particleCount: 50,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#2563EB', '#FFD700', '#FFFFFF'],
        zIndex: 10000
      });
      confetti({
        particleCount: 50,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#2563EB', '#FFD700', '#FFFFFF'],
        zIndex: 10000
      });
    }, 1000);
  };

  if (!isVisible) return null;

  const getIcon = () => {
    switch (type) {
      case 'post_test':
        return <Award className="w-16 h-16 text-yellow-500" />;
      case 'module':
        return <Star className="w-16 h-16 text-yellow-500 fill-yellow-500" />;
      default:
        return <Trophy className="w-16 h-16 text-yellow-500" />;
    }
  };

  const getMessage = () => {
    switch (type) {
      case 'post_test':
        return `You passed the Post-Test with ${score}%!`;
      case 'module':
        return `You completed the module successfully!`;
      default:
        return message;
    }
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop with gradient - Blue theme */}
      <div className="absolute inset-0 bg-gradient-to-b from-blue-900/80 via-blue-800/70 to-blue-900/80 backdrop-blur-sm" />
      
      {/* Floating balloons animation - Blue and Gold theme */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(12)].map((_, i) => (
          <div
            key={i}
            className="absolute animate-float-balloon"
            style={{
              left: `${Math.random() * 100}%`,
              bottom: '-100px',
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${4 + Math.random() * 3}s`
            }}
          >
            <div 
              className="w-12 h-14 rounded-full relative"
              style={{ 
                backgroundColor: ['#2563EB', '#3B82F6', '#FFD700', '#FFC107', '#FFFFFF', '#60A5FA'][i % 6],
                boxShadow: 'inset -5px -5px 10px rgba(0,0,0,0.2), inset 5px 5px 10px rgba(255,255,255,0.3)'
              }}
            >
              {/* Balloon shine */}
              <div className="absolute top-2 left-2 w-3 h-3 bg-white/40 rounded-full" />
              {/* Balloon string */}
              <div className="absolute -bottom-8 left-1/2 w-px h-8 bg-white/50 transform -translate-x-1/2" />
            </div>
          </div>
        ))}
      </div>

      {/* Ribbons falling animation - Blue and Gold */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={`ribbon-${i}`}
            className="absolute animate-fall-ribbon"
            style={{
              left: `${Math.random() * 100}%`,
              top: '-50px',
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${3 + Math.random() * 2}s`
            }}
          >
            <div 
              className="w-2 h-16 rounded-sm transform rotate-12"
              style={{ 
                backgroundColor: ['#FFD700', '#2563EB', '#3B82F6', '#FFFFFF', '#FFC107'][i % 5],
                opacity: 0.8
              }}
            />
          </div>
        ))}
      </div>

      {/* Main content card */}
      <div 
        className={`relative z-10 bg-white dark:bg-gray-900 rounded-3xl p-8 max-w-md mx-4 text-center shadow-2xl transform transition-all duration-500 ${
          showContent ? 'scale-100 opacity-100' : 'scale-50 opacity-0'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Decorative top border - Blue and Gold gradient */}
        <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-blue-600 via-yellow-500 to-blue-600 rounded-t-3xl" />
        
        {/* Party poppers - Blue and Gold */}
        <div className="absolute -top-4 -left-4">
          <PartyPopper className="w-10 h-10 text-blue-500 transform -rotate-45 animate-wiggle" />
        </div>
        <div className="absolute -top-4 -right-4">
          <PartyPopper className="w-10 h-10 text-yellow-500 transform rotate-45 animate-wiggle" style={{ animationDelay: '0.5s' }} />
        </div>

        {/* Trophy/Icon with glow - Gold theme */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-24 h-24 bg-yellow-400/30 rounded-full animate-pulse" />
          </div>
          <div className="relative flex items-center justify-center animate-bounce-slow">
            {getIcon()}
          </div>
        </div>

        {/* Stars decoration - Gold color */}
        <div className="flex justify-center gap-1 mb-4">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              className="w-6 h-6 text-yellow-500 fill-yellow-500 animate-twinkle"
              style={{ animationDelay: `${i * 0.1}s` }}
            />
          ))}
        </div>

        {/* Title */}
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 animate-fade-in">
          {title}
        </h2>

        {/* Course name - Blue theme */}
        {courseName && (
          <p className="text-lg font-semibold text-blue-600 dark:text-blue-400 mb-2 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            "{courseName}"
          </p>
        )}

        {/* Message */}
        <p className="text-gray-600 dark:text-gray-300 mb-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
          {getMessage()}
        </p>

        {/* Score display - Blue gradient */}
        {score !== undefined && (
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl py-3 px-6 mb-6 inline-block animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <span className="text-2xl font-bold">{score}%</span>
            <span className="ml-2 text-sm opacity-90">Score Achieved</span>
          </div>
        )}

        {/* Achievement badge - Gold gradient */}
        <div className="flex justify-center mb-6">
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white rounded-full py-2 px-4 text-sm font-medium flex items-center gap-2 animate-fade-in" style={{ animationDelay: '0.5s' }}>
            <Award className="w-4 h-4" />
            Achievement Unlocked!
          </div>
        </div>

        {/* Continue button - Blue theme */}
        <Button
          onClick={onClose}
          className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white py-3 rounded-xl font-semibold text-lg transform transition-transform hover:scale-105 animate-fade-in"
          style={{ animationDelay: '0.6s' }}
          data-testid="celebration-continue-btn"
        >
          Continue
        </Button>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes float-balloon {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-120vh) rotate(15deg);
            opacity: 0;
          }
        }
        
        @keyframes fall-ribbon {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          100% {
            transform: translateY(120vh) rotate(360deg);
            opacity: 0;
          }
        }
        
        @keyframes wiggle {
          0%, 100% { transform: rotate(-45deg) scale(1); }
          25% { transform: rotate(-35deg) scale(1.1); }
          75% { transform: rotate(-55deg) scale(1.1); }
        }
        
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes twinkle {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(0.8); }
        }
        
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-float-balloon {
          animation: float-balloon 6s ease-in-out forwards;
        }
        
        .animate-fall-ribbon {
          animation: fall-ribbon 4s linear forwards;
        }
        
        .animate-wiggle {
          animation: wiggle 0.5s ease-in-out infinite;
        }
        
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        
        .animate-twinkle {
          animation: twinkle 1s ease-in-out infinite;
        }
        
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
          opacity: 0;
        }
      `}</style>
    </div>
  );
};

export default CelebrationOverlay;
